import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ExternalLink, Loader2, Pencil, Plus, RefreshCw, Sparkles, X } from "lucide-react";
import { Input } from "@foundation/components/ui/input";
import { Textarea } from "@foundation/components/ui/textarea";
import { Badge } from "@foundation/components/ui/badge";
import { Button } from "@foundation/components/ui/button";
import type { BoostPolicyBrandingData, BoostPolicyFormData, OrganizationProfileData } from "../../types/boostPolicy";
import { extractRelevantContext, getOrgMemory } from "../../services/orgMemoryService";

const INDUSTRY_LABELS: Record<string, string> = {
  manufacturing: 'Manufacturing',
  construction: 'Construction',
  healthcare: 'Healthcare',
  oil_gas: 'Oil & Gas',
  mining: 'Mining',
  transport_logistics: 'Transportation & Logistics',
  food_beverage: 'Food & Beverage',
  utilities: 'Utilities',
  government: 'Government',
  professional_services: 'Professional Services',
  other: 'Other',
};

const SIZE_LABELS: Record<string, string> = {
  '1-10': '1–10 employees',
  '11-50': '11–50 employees',
  '51-200': '51–200 employees',
  '201-500': '201–500 employees',
  '500+': '500+ employees',
};

const MATURITY_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

const DEFAULT_BRANDING: BoostPolicyBrandingData = {
  primary_color: '#000000',
  secondary_color: '#000000',
  font_color: '#000000',
  font_family: 'Inter',
};

type ColorSwatchProps = { label: string; value: string | undefined };

const ColorSwatch = ({ label, value }: ColorSwatchProps) => {
  const color = value || '#000000';
  return (
    <div>
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2 mt-1">
        <div className="h-5 w-5 rounded border border-border flex-shrink-0" style={{ backgroundColor: color }} />
        <span className="text-sm font-mono text-foreground">{color}</span>
      </div>
    </div>
  );
};

type OrgProfileDisplayProps = {
  profile: OrganizationProfileData | null;
  isLoading: boolean;
  orgId: string;
};

const OrgProfileDisplay = ({ profile, isLoading, orgId }: OrgProfileDisplayProps) => {
  const navigate = useNavigate();
  const orgProfileUrl = `/organisation/${orgId}?tab=branding&brandingTab=profile`;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading organisation profile…
      </div>
    );
  }

  const hasProfile =
    profile?.industry || profile?.org_size || profile?.maturityLevel || profile?.jurisdiction || profile?.standards?.length;

  if (!hasProfile) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/30 px-4 py-4 text-sm text-muted-foreground flex items-start gap-2">
        <Building2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <span>
          No organisation profile set.{' '}
          <button type="button" onClick={() => navigate(orgProfileUrl)} className="text-primary hover:underline inline-flex items-center gap-0.5">
            Set it up in Organisation → Branding → Org Profile
            <ExternalLink className="h-3 w-3 ml-0.5" />
          </button>
        </span>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card divide-y divide-border">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 px-4 py-4">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Industry</p>
          {profile?.industry ? (
            <Badge variant="secondary" className="text-sm font-normal">
              {INDUSTRY_LABELS[profile.industry] ?? profile.industry}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </div>
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Organisation Size</p>
          {profile?.org_size ? (
            <Badge variant="secondary" className="text-sm font-normal">
              {SIZE_LABELS[profile.org_size] ?? profile.org_size}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </div>
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Governance Maturity</p>
          {profile?.maturityLevel ? (
            <Badge variant="secondary" className="text-sm font-normal">
              {MATURITY_LABELS[profile.maturityLevel] ?? profile.maturityLevel}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </div>
      </div>

      {profile?.jurisdiction && (
        <div className="px-4 py-4 space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Primary Jurisdiction</p>
          <p className="text-sm text-foreground">{profile.jurisdiction}</p>
        </div>
      )}

      {profile?.standards && profile.standards.length > 0 && (
        <div className="px-4 py-4 space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Standards & Certifications</p>
          <div className="flex flex-wrap gap-2">
            {profile.standards.map((s) => (
              <Badge key={s} variant="outline" className="text-xs border-primary/40 text-primary bg-primary/5">
                {s}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="px-4 py-2.5 bg-muted/30 flex items-center justify-end">
        <button
          type="button"
          onClick={() => navigate(orgProfileUrl)}
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors"
        >
          Manage in Organisation → Branding → Org Profile
          <ExternalLink className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
};

type RelevantContextSectionProps = {
  items: string[];
  onChange: (items: string[]) => void;
  orgId: string;
  orgProfile: OrganizationProfileData | null;
  documentTitle: string;
  documentTags: string[];
};

const RelevantContextSection = ({
  items: itemsProp,
  onChange,
  orgId,
  orgProfile,
  documentTitle,
  documentTags,
}: RelevantContextSectionProps) => {
  const items = itemsProp ?? [];
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const editRef = useRef<HTMLTextAreaElement>(null);
  const hasFetchedRef = useRef(false);

  // Clear editing state when the items array is replaced (e.g. after Refresh)
  useEffect(() => {
    setEditingIndex(null);
  }, [itemsProp]);

  const runExtraction = async () => {
    setIsExtracting(true);
    setExtractError(null);
    try {
      const memory = await getOrgMemory(orgId);
      if (!memory.trim()) {
        setExtractError('No Q&A history found. Complete the AI Context step first to populate this.');
        setIsExtracting(false);
        return;
      }
      const extracted = await extractRelevantContext(memory, documentTitle, documentTags, orgProfile, orgId);
      onChange(extracted);
    } catch (err) {
      console.error('[RelevantContextSection] Extraction failed:', err);
      setExtractError('Could not extract context. You can add items manually below.');
    } finally {
      setIsExtracting(false);
    }
  };

  // Auto-extract on mount if no items yet
  useEffect(() => {
    if (hasFetchedRef.current || items.length > 0) return;
    hasFetchedRef.current = true;
    runExtraction();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEdit = (index: number) => {
    setEditingIndex(index);
    setEditValue(items[index]);
    setTimeout(() => editRef.current?.focus(), 0);
  };

  const commitEdit = () => {
    if (editingIndex === null) return;
    const trimmed = editValue.trim();
    if (trimmed) {
      const updated = [...items];
      updated[editingIndex] = trimmed;
      onChange(updated);
    }
    setEditingIndex(null);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const addItem = () => {
    const updated = [...items, ''];
    onChange(updated);
    setEditingIndex(updated.length - 1);
    setEditValue('');
    setTimeout(() => editRef.current?.focus(), 0);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#33b864]" />
          <h3 className="text-base font-semibold text-foreground">Relevant Context</h3>
          <Badge variant="outline" className="text-xs border-[#33b864]/40 text-[#33b864] bg-[#33b864]/5">
            AI extracted
          </Badge>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={runExtraction}
          disabled={isExtracting}
          className="text-xs text-muted-foreground hover:text-foreground gap-1.5"
        >
          {isExtracting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Refresh
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        Key context pulled from your Q&amp;A history, filtered to what matters for this policy. Edit or add items to refine.
      </p>

      {isExtracting && items.length === 0 && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 rounded-lg bg-muted/50 animate-pulse" />
          ))}
        </div>
      )}

      {!isExtracting && extractError && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 px-4 py-4 text-sm text-muted-foreground flex items-start gap-2">
          <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0 opacity-40" />
          <span>{extractError}</span>
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((item, idx) => (
            <div key={idx} className="group flex items-start gap-2">
              {editingIndex === idx ? (
                <Textarea
                  ref={editRef}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitEdit(); }
                    if (e.key === 'Escape') { setEditingIndex(null); }
                  }}
                  rows={2}
                  className="flex-1 text-sm resize-none"
                />
              ) : (
                <div className="flex-1 flex items-start gap-2 rounded-lg border border-border bg-card px-3 py-2.5">
                  <span className="flex-1 text-sm text-foreground leading-snug">{item}</span>
                  <button
                    type="button"
                    onClick={() => startEdit(idx)}
                    className="flex-shrink-0 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-0.5"
                    title="Edit"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => removeItem(idx)}
                className="flex-shrink-0 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity mt-2.5"
                title="Remove"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {extractError && items.length > 0 && (
        <p className="text-xs text-destructive">{extractError}</p>
      )}

      <button
        type="button"
        onClick={addItem}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mt-1"
      >
        <Plus className="h-3.5 w-3.5" />
        Add context item
      </button>
    </div>
  );
};

export type PolicySetupStepProps = {
  data: BoostPolicyFormData;
  onChange: (field: keyof BoostPolicyFormData, value: string | string[]) => void;
  orgId: string;
  brandingData: BoostPolicyBrandingData | null;
  orgProfile: OrganizationProfileData | null;
  profileLoading: boolean;
  brandingLoading: boolean;
};

const buildSuggestedName = (orgName: string | undefined, docTitle: string | undefined): string => {
  const org = (orgName ?? '').trim().replace(/\s+/g, '_');
  // Strip common file suffixes and clean the document title
  const doc = (docTitle ?? '')
    .replace(/(_KB)?\.md$/i, '')
    .replace(/\s+/g, '_')
    .trim();
  if (!org && !doc) return '';
  if (!org) return doc;
  if (!doc) return org;
  return `${org}_${doc}`;
};

export const PolicySetupStep = ({
  data,
  onChange,
  orgId,
  brandingData,
  orgProfile,
  profileLoading,
  brandingLoading,
}: PolicySetupStepProps) => {
  const navigate = useNavigate();

  const suggestedName = buildSuggestedName(
    brandingData?.brand_title as string | undefined,
    data.selectedKnowledgeDocument?.title,
  );

  return (
    <div className="space-y-8">
      {/* 1. Policy Name — first, mandatory */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground block">
          What do you want to name your policy?
          <span className="text-destructive ml-1">*</span>
        </label>
        <Input
          value={data.policyName}
          onChange={(e) => onChange("policyName", e.target.value)}
          placeholder="e.g. Workplace Health and Safety Policy"
          maxLength={200}
          required
        />
        {suggestedName && !data.policyName.trim() && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">Suggested:</span>
            <button
              type="button"
              onClick={() => onChange("policyName", suggestedName)}
              className="text-xs px-2.5 py-1 rounded-full border border-[#33b864]/40 bg-[#33b864]/5 text-[#33b864] hover:bg-[#33b864]/15 transition-colors font-mono"
            >
              {suggestedName}
            </button>
          </div>
        )}
        {!data.policyName.trim() && !suggestedName && (
          <p className="text-xs text-muted-foreground">A policy name is required to continue.</p>
        )}
      </div>

      {/* 2. Reference policy */}
      <div className="rounded-lg border border-border bg-card p-3">
        <p className="text-xs text-muted-foreground mb-1">Reference policy selected</p>
        <p className="text-sm font-medium text-foreground">
          {data.selectedKnowledgeDocument?.title || "No reference policy selected"}
        </p>
        {data.selectedKnowledgeDocument?.tags && data.selectedKnowledgeDocument.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {data.selectedKnowledgeDocument.tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs px-2 py-0.5">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-border" />

      {/* 3. Organisation Profile */}
      <div className="space-y-3">
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          Organisation Profile
        </h3>
        <p className="text-sm text-muted-foreground">
          Used by the AI to tailor compliance language, jurisdiction references, and standard citations.
        </p>
        <OrgProfileDisplay profile={orgProfile} isLoading={profileLoading} orgId={orgId} />
      </div>

      <div className="border-t border-border" />

      {/* 4. Relevant Context — AI-extracted from memory.md, editable */}
      <RelevantContextSection
        items={data.relevantContext ?? []}
        onChange={(items) => onChange("relevantContext", items)}
        orgId={orgId}
        orgProfile={orgProfile}
        documentTitle={data.selectedKnowledgeDocument?.title ?? ''}
        documentTags={data.selectedKnowledgeDocument?.tags ?? []}
      />

      <div className="border-t border-border" />

      {/* 5. Branding */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-foreground">Branding</h3>
          <button
            type="button"
            onClick={() => navigate(`/organisation/${orgId}?tab=branding`)}
            className="inline-flex items-center gap-1 text-xs text-[#33b864] font-medium hover:opacity-80 transition-opacity"
          >
            Manage in Organisation → Branding
            <ExternalLink className="h-3 w-3" />
          </button>
        </div>

        {brandingLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading branding…
          </div>
        ) : (
          <>
            {(() => {
              // Always show branding values. When the org hasn't configured
              // branding (or only configured some fields), fall back to defaults
              // per-field so the colour swatches are never blank.
              const source = brandingData ?? DEFAULT_BRANDING;
              const b = {
                ...source,
                primary_color: source.primary_color || DEFAULT_BRANDING.primary_color,
                secondary_color: source.secondary_color || DEFAULT_BRANDING.secondary_color,
                font_color: source.font_color || DEFAULT_BRANDING.font_color,
                font_family: source.font_family || DEFAULT_BRANDING.font_family,
              };
              return (
                <div className="space-y-4">
                  {b.logo_url && (
                    <div>
                      <span className="text-xs text-muted-foreground block mb-1.5">Logo</span>
                      <img
                        src={b.logo_url as string}
                        alt="Organisation logo"
                        className="h-14 object-contain rounded border border-border bg-muted p-1"
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-4">
                    <ColorSwatch label="Primary colour" value={b.primary_color as string} />
                    <ColorSwatch label="Secondary colour" value={b.secondary_color as string} />
                    <ColorSwatch label="Font colour" value={b.font_color as string} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-muted-foreground block">Font family</span>
                      <p className="text-sm mt-0.5 text-foreground">{(b.font_family as string) || 'Inter'}</p>
                    </div>
                    {b.tagline && (
                      <div>
                        <span className="text-xs text-muted-foreground block">Tagline</span>
                        <p className="text-sm mt-0.5 text-foreground italic">{b.tagline as string}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
};
